import { useState } from "react";
import { MAX_INSTRUMENT_SLOT_COUNT } from "../../../../src/lib/audio";
import { useStoreSelector } from "../../../../src/lib/store";
import { LAB_DEMOS } from "../lib/demos";
import { MATERIAL_OPTIONS } from "../lib/material";
import { getLabActions } from "../labActions";
import { projectStore, transportStore, uiStore } from "../store";
import {
  LabMenuItem,
  LabMenuRadio,
  LabMenuSeparator,
  LabMenuToggle,
  LabToolbarMenu,
} from "./LabToolbarMenu";
import { tbGroup, tbSep } from "./toolbarChrome";

type MenuId = "file" | "track" | "demo" | "dsl" | "view" | "theme" | null;

/**
 * Curva-style toolbar menus (controlled + AnchoredMenu), replacing `<details>`.
 */
export function ToolbarMenus() {
  const [openMenu, setOpenMenu] = useState<MenuId>(null);
  const armed = useStoreSelector(transportStore, s => s.armed);
  const instrumentOpen = useStoreSelector(uiStore, s => s.instrumentSlot !== null);
  const fxOpen = useStoreSelector(uiStore, s => s.fxOpen);
  const patternsOpen = useStoreSelector(uiStore, s => s.patternsOpen);
  const samplesOpen = useStoreSelector(uiStore, s => s.samplesOpen);
  const dslOpen = useStoreSelector(uiStore, s => s.dslOpen);
  const statusOpen = useStoreSelector(uiStore, s => s.statusOpen);
  const mixerOpen = useStoreSelector(uiStore, s => s.mixerOpen);
  const performanceOpen = useStoreSelector(uiStore, s => s.performanceOpen);
  const material = useStoreSelector(uiStore, s => s.material);
  const helpOpen = useStoreSelector(uiStore, s => s.helpOpen);
  const goToOpen = useStoreSelector(uiStore, s => s.goToOpen);
  const trackCount = useStoreSelector(projectStore, s => s.instruments.length);

  function openOnly(id: MenuId): void {
    setOpenMenu(id);
  }

  function run(action: () => void): void {
    setOpenMenu(null);
    action();
  }

  return (
    <>
      <div className={tbGroup}>
        <LabToolbarMenu
          label="File"
          title="Project file"
          open={openMenu === "file"}
          onOpenChange={open => openOnly(open ? "file" : null)}
        >
          <LabMenuItem label="New" title="New empty 8-track pattern (keeps bank & FX)" onClick={() => run(() => getLabActions()?.newProject())} />
          <LabMenuItem label="Import…" title="Import .adam project (or legacy JSON)" onClick={() => run(() => void getLabActions()?.importProject())} />
          <LabMenuItem label="Export" title="Export project as .adam" onClick={() => run(() => getLabActions()?.exportProject())} />
          <LabMenuItem label="Import MIDI…" title="Import a .mid/.midi file as a full pattern + instruments" onClick={() => run(() => void getLabActions()?.importMidi())} />
          <LabMenuItem label="Export MIDI…" title="Export the arrangement (all patterns in order) as a Standard MIDI File" onClick={() => run(() => getLabActions()?.exportMidi())} />
          <LabMenuItem
            label="Bounce selected patterns"
            title="Bounce the loop brace to WAV (offline, with FX tail)"
            disabled={!armed}
            onClick={() => run(() => void getLabActions()?.bounce("range"))}
          />
          <LabMenuItem
            label="Bounce to sampler"
            title="Bounce the loop brace into a new sampler track and open Author"
            disabled={!armed}
            onClick={() => run(() => void getLabActions()?.bounceToSampler())}
          />
          <LabMenuItem
            label="Bounce project"
            title="Bounce full project (pattern order) to WAV"
            disabled={!armed}
            onClick={() => run(() => void getLabActions()?.bounce("project"))}
          />
          <LabMenuItem
            label="Stems selected patterns…"
            title="Export loop brace stems as a ZIP of WAVs"
            disabled={!armed}
            onClick={() => run(() => void getLabActions()?.bounceStems("range"))}
          />
          <LabMenuItem
            label="Stems project…"
            title="Export project stems as a ZIP of WAVs"
            disabled={!armed}
            onClick={() => run(() => void getLabActions()?.bounceStems("project"))}
          />
          <LabMenuSeparator />
          <LabMenuItem
            label="Settings…"
            title="MIDI bridge and control bindings"
            onClick={() => run(() => getLabActions()?.toggleSettings())}
          />
        </LabToolbarMenu>

        {/*<LabToolbarMenu*/}
        {/*  label="Track"*/}
        {/*  title="Instrument tracks"*/}
        {/*  open={openMenu === "track"}*/}
        {/*  onOpenChange={open => openOnly(open ? "track" : null)}*/}
        {/*>*/}
        {/*  <LabMenuItem*/}
        {/*    label="Add track"*/}
        {/*    title={*/}
        {/*      trackCount >= MAX_INSTRUMENT_SLOT_COUNT*/}
        {/*        ? `Maximum ${MAX_INSTRUMENT_SLOT_COUNT} tracks`*/}
        {/*        : `Add instrument track (${trackCount} → ${trackCount + 1})`*/}
        {/*    }*/}
        {/*    disabled={trackCount >= MAX_INSTRUMENT_SLOT_COUNT}*/}
        {/*    onClick={() => run(() => getLabActions()?.addTrack())}*/}
        {/*  />*/}
        {/*</LabToolbarMenu>*/}

        <LabToolbarMenu
          label="Demo"
          title="Load a demo pattern"
          open={openMenu === "demo"}
          onOpenChange={open => openOnly(open ? "demo" : null)}
        >
          {LAB_DEMOS.map(demo => (
            <LabMenuItem
              key={demo.id}
              label={demo.label}
              onClick={() => run(() => getLabActions()?.loadDemo(demo.id))}
            />
          ))}
        </LabToolbarMenu>

        {/*<LabToolbarMenu*/}
        {/*  label="DSL"*/}
        {/*  title="Pattern DSL"*/}
        {/*  open={openMenu === "dsl"}*/}
        {/*  onOpenChange={open => openOnly(open ? "dsl" : null)}*/}
        {/*>*/}
        {/*  <LabMenuItem*/}
        {/*    label="DSL → Grid"*/}
        {/*    title="Apply DSL → grid"*/}
        {/*    disabled={!armed}*/}
        {/*    onClick={() => run(() => getLabActions()?.applyDsl())}*/}
        {/*  />*/}
        {/*  <LabMenuItem*/}
        {/*    label="Grid → DSL"*/}
        {/*    title="Apply grid → DSL"*/}
        {/*    disabled={!armed}*/}
        {/*    onClick={() => run(() => getLabActions()?.applyGridToDsl())}*/}
        {/*  />*/}
        {/*</LabToolbarMenu>*/}
      </div>

      <div className={tbSep} role="separator" />

      <div className={tbGroup}>
        <LabToolbarMenu
          label="View"
          title="View"
          open={openMenu === "view"}
          onOpenChange={open => openOnly(open ? "view" : null)}
          align="right"
        >
          <LabMenuToggle
            label={instrumentOpen ? "Hide instruments panel" : "Show instruments panel"}
            checked={instrumentOpen}
            onClick={() => getLabActions()?.toggleInstrument()}
          />
          <LabMenuToggle
            label={fxOpen ? "Hide FX panel" : "Show FX panel"}
            checked={fxOpen}
            onClick={() => getLabActions()?.toggleFx()}
          />
          <LabMenuToggle
            label={patternsOpen ? "Hide arrange panel" : "Show arrange panel"}
            checked={patternsOpen}
            onClick={() => getLabActions()?.togglePatterns()}
          />
          <LabMenuToggle
            label={samplesOpen ? "Hide samples panel" : "Show samples panel"}
            checked={samplesOpen}
            onClick={() => getLabActions()?.toggleSamples()}
          />
          <LabMenuSeparator />
          <LabMenuToggle
            label={mixerOpen ? "Hide mixer" : "Show mixer"}
            checked={mixerOpen}
            onClick={() => getLabActions()?.toggleMixer()}
          />
          <LabMenuToggle
            label={performanceOpen ? "Hide performance" : "Show performance"}
            checked={performanceOpen}
            onClick={() => getLabActions()?.togglePerformance()}
          />
          <LabMenuSeparator />
          <LabMenuToggle
            label={dslOpen ? "Hide DSL panel" : "Show DSL panel"}
            checked={dslOpen}
            onClick={() => getLabActions()?.toggleDslPanel()}
          />
          <LabMenuToggle
            label={statusOpen ? "Hide status bar" : "Show status bar"}
            checked={statusOpen}
            onClick={() => getLabActions()?.toggleStatusPanel()}
          />
        </LabToolbarMenu>

        <LabToolbarMenu
          label="Theme"
          title="Chrome material theme"
          open={openMenu === "theme"}
          onOpenChange={open => openOnly(open ? "theme" : null)}
          align="right"
        >
          {MATERIAL_OPTIONS.map(option => (
            <LabMenuRadio
              key={option.id}
              label={option.label}
              checked={material === option.id}
              onClick={() => run(() => getLabActions()?.setMaterial(option.id))}
            />
          ))}
        </LabToolbarMenu>

        <button
          type="button"
          className={`tb-icon${goToOpen ? " active" : ""}`}
          title="Go to row / marker (Shift Shift)"
          aria-label="Go to row or marker"
          aria-pressed={goToOpen}
          onClick={() => getLabActions()?.toggleGoTo()}
        >
          <svg viewBox="0 0 24 24" width={20} height={20} aria-hidden={true}>
            <circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" strokeWidth="2" />
            <path d="M16 16l4.5 4.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M8.5 11h5M11 8.5v5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        <button
          type="button"
          className={`tb-icon${helpOpen ? " active" : ""}`}
          title="Tracker help"
          aria-label="Tracker help"
          aria-pressed={helpOpen}
          onClick={() => getLabActions()?.toggleHelp()}
        >
          <svg viewBox="0 0 24 24" width={20} height={20} aria-hidden={true}>
            <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
            <path d="M9.5 9.5a2.5 2.5 0 1 1 3.6 2.25c-.7.4-1.1.9-1.1 1.75V14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <circle cx="12" cy="17" r="1" fill="currentColor" />
          </svg>
        </button>
      </div>
    </>
  );
}
